

import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Video,
  Star,
  ExternalLink,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { instagramReelsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface InstagramReel {
  id: number;
  url: string;
  is_active: boolean;
  is_selected: boolean;
  sort_order: number;
  created_at?: string;
}

function StatBadge({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className={`px-3 py-1.5 rounded-lg ${color} flex items-center gap-2`}>
      <span className="text-xs font-medium opacity-80">{label}:</span>
      <span className="text-sm font-bold">{value}</span>
    </div>
  );
}

function ReelForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<InstagramReel>;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [url, setUrl] = useState(initialData?.url || "");
  const [is_active, setIsActive] = useState(initialData?.is_active ?? true);
  const [is_selected, setIsSelected] = useState(initialData?.is_selected ?? false);
  const [sort_order, setSortOrder] = useState(initialData?.sort_order || 0);

  const getEmbedUrl = (url: string) => {
    if (!url) return null;
    const reelMatch = url.match(/instagram\.com\/(?:reel|p)\/([^/?]+)/);
    if (reelMatch) {
      return `https://www.instagram.com/reel/${reelMatch[1]}/embed`;
    }
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      url,
      is_active,
      is_selected,
      sort_order: Number(sort_order),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Instagram Reel URL *</label>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.instagram.com/reel/..."
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
        <p className="text-xs text-stone-400 mt-1">Paste the full Instagram reel URL</p>
      </div>

      {url && getEmbedUrl(url) && (
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Preview</label>
          <div className="bg-stone-50 rounded-xl p-3 border border-stone-200">
            <iframe
              src={getEmbedUrl(url) || ""}
              className="w-full h-64 rounded-lg"
              frameBorder="0"
              scrolling="no"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Sort Order</label>
          <input
            type="number"
            value={sort_order}
            onChange={(e) => setSortOrder(Number(e.target.value))}
            min="0"
            className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Active</label>
          <button
            type="button"
            onClick={() => setIsActive(!is_active)}
            className={`w-full px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
              is_active
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-stone-50 border-stone-200 text-stone-500"
            }`}
          >
            {is_active ? "Yes" : "No"}
          </button>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Homepage</label>
          <button
            type="button"
            onClick={() => setIsSelected(!is_selected)}
            className={`w-full px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
              is_selected
                ? "bg-amber-50 border-amber-200 text-amber-700"
                : "bg-stone-50 border-stone-200 text-stone-500"
            }`}
          >
            {is_selected ? "Yes" : "No"}
          </button>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Add"} Reel
        </button>
      </div>
    </form>
  );
}

export default function ReelsManager() {
  const { showToast } = useToast();

  const [reels, setReels] = useState<InstagramReel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReel, setEditingReel] = useState<InstagramReel | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingReel, setDeletingReel] = useState<InstagramReel | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await instagramReelsApi.list();
      setReels(response.data?.data?.data || response.data?.data || []);
    } catch (error) {
      showToast("Failed to load Instagram reels", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredReels = useMemo(() => {
    if (!searchQuery) return reels;
    const q = searchQuery.toLowerCase();
    return reels.filter((r) => r.url.toLowerCase().includes(q));
  }, [reels, searchQuery]);

  const stats = useMemo(
    () => ({
      total: reels.length,
      active: reels.filter((r) => r.is_active).length,
      selected: reels.filter((r) => r.is_selected).length,
    }),
    [reels]
  );

  const moveItem = async (reel: InstagramReel, direction: 'up' | 'down') => {
    const currentIndex = filteredReels.findIndex(r => r.id === reel.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= filteredReels.length) return;
    
    const reelA = filteredReels[currentIndex];
    const reelB = filteredReels[targetIndex];
    
    try {
      await Promise.all([
        instagramReelsApi.update(reelA.id, { sort_order: reelB.sort_order }),
        instagramReelsApi.update(reelB.id, { sort_order: reelA.sort_order }),
      ]);
      showToast("Order updated", "success");
      loadData();
    } catch (error) {
      showToast("Failed to update order", "error");
    }
  };

  const handleDelete = async () => {
    if (!deletingReel) return;
    setDeleteLoading(true);
    try {
      await instagramReelsApi.delete(deletingReel.id);
      showToast("Instagram reel deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingReel(null);
      loadData();
    } catch (error) {
      showToast("Failed to delete Instagram reel", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    setIsModalOpen(false);
    setEditingReel(null);
    try {
      if (editingReel) {
        await instagramReelsApi.update(editingReel.id, data);
        showToast("Instagram reel updated successfully", "success");
      } else {
        await instagramReelsApi.create(data);
        showToast("Instagram reel created successfully", "success");
      }
      loadData();
    } catch (error) {
      showToast("Failed to save Instagram reel", "error");
    }
  };

  const handleToggleSelected = async (reel: InstagramReel) => {
    try {
      await instagramReelsApi.toggleSelected(reel.id);
      setReels((p) =>
        p.map((r) => (r.id === reel.id ? { ...r, is_selected: !r.is_selected } : r))
      );
      showToast("Selection updated", "success");
    } catch (error) {
      showToast("Failed to update selection", "error");
    }
  };

  const handleToggleActive = async (reel: InstagramReel) => {
    try {
      await instagramReelsApi.toggleActive(reel.id);
      setReels((p) =>
        p.map((r) => (r.id === reel.id ? { ...r, is_active: !r.is_active } : r))
      );
      showToast("Status updated", "success");
    } catch (error) {
      showToast("Failed to update status", "error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="flex flex-wrap gap-2 mb-4">
        <StatBadge label="Total" value={stats.total} color="bg-stone-100 text-stone-700" />
        <StatBadge label="Active" value={stats.active} color="bg-emerald-100 text-emerald-700" />
        <StatBadge label="Homepage" value={stats.selected} color="bg-amber-100 text-amber-700" />
      </div>

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by URL..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => {
            setEditingReel(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Reel
        </button>
      </div>

      {filteredReels.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Video className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No Instagram reels found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredReels.map((reel, index) => (
            <div key={reel.id} className="bg-white rounded-xl border border-stone-200 p-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center flex-shrink-0">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-stone-800 text-sm">Instagram Reel</h3>
                    <span className="text-xs text-stone-400">#{reel.sort_order}</span>
                  </div>
                  <a
                    href={reel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                  >
                    View on Instagram <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveItem(reel, 'up')}
                    disabled={index === 0}
                    className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 disabled:opacity-30"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveItem(reel, 'down')}
                    disabled={index === filteredReels.length - 1}
                    className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 disabled:opacity-30"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(reel)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      reel.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"
                    }`}
                  >
                    {reel.is_active ? "Active" : "Inactive"}
                  </button>
                  <button
                    onClick={() => handleToggleSelected(reel)}
                    className={`p-1 rounded ${reel.is_selected ? "text-amber-500" : "text-stone-300"}`}
                  >
                    <Star className={`w-4 h-4 ${reel.is_selected ? "fill-current" : ""}`} />
                  </button>
                  <button
                    onClick={() => { setEditingReel(reel); setIsModalOpen(true); }}
                    className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-100"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setDeletingReel(reel); setDeleteModalOpen(true); }}
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingReel(null);
        }}
        title={editingReel ? "Edit Instagram Reel" : "Add New Instagram Reel"}
        size="md"
      >
        <ReelForm
          initialData={editingReel || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingReel(null);
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingReel(null);
        }}
        onConfirm={handleDelete}
        title="Delete Instagram Reel"
        message="Are you sure you want to delete this Instagram reel? This action cannot be undone."
        itemName={deletingReel?.url}
        loading={deleteLoading}
      />
    </div>
  );
}
